import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ThemePalette } from 'mui.theme';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import CircularProgressChart from 'views/Production/components/CircularProgress/CircularProgress';
import WarningIcon from '@mui/icons-material/Warning';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import type { totalFleetDetails } from 'types/production.types';
import { percentageLeft, formatNumberToShortScale } from './utils';
import TargetSetter from 'views/Utilization/components/ProductiveHours/TargetSetter';
import { useAppSelector } from 'store/hook';
import { useEffect, useState } from 'react';
import TargetHandler from 'handlers/targets.handler';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { truncateTo2dp } from 'utils/helpers/general';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  isLoading: boolean;
  unitSystem: string;
  data: totalFleetDetails;
  rollingDailyAverage: number;
  currentDailyAverage: number;
  isLoadingFleetPeriodSummary: boolean;
  setDetailsVal: any;
}

const HaulerProductionDetails = ({
  enableDarkTheme,
  isLoading,
  unitSystem,
  data,
  rollingDailyAverage,
  currentDailyAverage,
  isLoadingFleetPeriodSummary,
  setDetailsVal,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const {
    data: {
      allFleet: { totalFleetDetails },
    },
  } = useAppSelector((state) => state.productionReducer);

  const target = useAppSelector((state) => state.targetReducer.data.production);

  const haulerProductionTarget = target?.find(
    (item) => item.assetType === 'hauler'
  );

  const hasMetTarget =
    data.haulerTon > (haulerProductionTarget?.targetValue as number);

  const customerId = useAppSelector((state) => state.persistedReducer).customer
    ?.id;

  const [fullYear] = useState(new Date().getFullYear());

  const targetHandler = new TargetHandler();
  const fetchTargets = async () => {
    try {
      await targetHandler.getTarget(
        customerId,
        'production',
        `hauler`,
        fullYear
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    void fetchTargets();
  }, [customerId]);

  useEffect(() => {
    if (!isLoadingFleetPeriodSummary) {
      setDetailsVal({
        'YTD Target (hours)': formatNumberToShortScale(
          convertToTwoDecimalPlaces(data.ytdTarget.toString())
        ),
        'Annual Production Target (hours)': formatNumberToShortScale(
          convertToTwoDecimalPlaces(data.annualTarget.toString())
        ),
        'Total Number of Vehicles': data.haulerCount,
        [`Rolling Daily Average 7 Days (Tons/Day)`]: formatNumberToShortScale(
          convertToTwoDecimalPlaces((rollingDailyAverage || 0).toString())
        ),
        'Total Production': formatNumberToShortScale(
          convertToTwoDecimalPlaces(data.haulerTon.toString())
        ),
        remaining_days: data.remainingDays,
        [`Current Daily Average 7 Days (Tons/Day)`]: formatNumberToShortScale(
          convertToTwoDecimalPlaces((currentDailyAverage ?? 0).toString())
        ),
        // ...(!hasMetTarget
        //   ? {
        //       'Avg Production needs to go up by': `${truncateTo2dp(
        //         targetPercentageDiff
        //       )}'%`,
        //     }
        //   : {}),
        'Target Met (True/False)': hasMetTarget,
      });
    }
  }, [
    isLoadingFleetPeriodSummary,
    data,
    rollingDailyAverage,
    currentDailyAverage,
    hasMetTarget,
  ]);

  return (
    <Grid item lg={6} md={6} sm={12}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
          minHeight: '300px',
          maxHeight: '300px',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: '600',
                fontSize: '13px',
              }}
            >
              {t('haulers_production_in_tons_ytd')}
            </Typography>
            <TargetSetter
              enableDarkTheme={enableDarkTheme}
              assetType={`hauler`}
              metricName="production"
            />
            {/* <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                }}
              >
                Mine Production Plan:
              </Typography>
              <Button
                size="small"
                sx={{
                  border: '1px solid #4A7BFA',
                  color: palette.textPrimary,
                  textTransform: 'none',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  fontSize: '11px',
                }}
                startIcon={<FileUploadIcon />}
              >
                Upload
              </Button>
            </Box> */}
          </Box>
        </Box>

        {isLoading ? (
          <MultiColorCircularLoader height="300px" />
        ) : (
          <Box
            sx={{
              paddingTop: '15px',
            }}
          >
            <Grid container>
              <Grid item lg={6} sm={12}>
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <CircularProgressChart
                    enableDarkTheme={enableDarkTheme}
                    value={data.haulerTon}
                    target={data.ytdTarget}
                    unitSystem={unitSystem}
                  />
                  <Box
                    sx={{
                      alignContent: 'center',
                      paddingLeft: '10px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textSecondary,
                      }}
                    >
                      {t('ytd_target')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textPrimary,
                        paddingBottom: '15px',
                      }}
                    >
                      {formatNumberToShortScale(
                        convertToTwoDecimalPlaces(data.ytdTarget.toString())
                      )}{' '}
                      {t('tons_unit')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textSecondary,
                      }}
                    >
                      {t('annual_target')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textPrimary,
                      }}
                    >
                      {formatNumberToShortScale(
                        convertToTwoDecimalPlaces(
                          (haulerProductionTarget?.targetValue ?? 0).toString()
                        )
                      )}{' '}
                      {t('tons_unit')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item lg={6} sm={12}>
                <Box>
                  <Grid container>
                    <Grid item lg={6} sm={12}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('total_vehicles')}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textPrimary,
                          paddingTop: '10px',
                        }}
                      >
                        {data.haulerCount}
                      </Typography>

                      <Typography
                        sx={{
                          paddingTop: '30px',
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('rolling_daily_average_7d')}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: palette.textPrimary,
                          paddingTop: '10px',
                        }}
                      >
                        {rollingDailyAverage > 0 ? (
                          isLoadingFleetPeriodSummary ? (
                            <Typography
                              sx={{
                                fontSize: '13px',
                                color: palette.textPrimary,
                                paddingTop: '10px',
                              }}
                            >
                              {t('cdp_loading')}
                            </Typography>
                          ) : (
                            <>
                              {formatNumberToShortScale(
                                truncateTo2dp(rollingDailyAverage)
                              )}
                              <span
                                style={{
                                  color: palette.textSecondary,
                                }}
                              >
                                {t('tons_per_day')}
                              </span>
                            </>
                          )
                        ) : (
                          <>{t('no_data_available')}</>
                        )}
                      </Typography>
                    </Grid>
                    <Grid item lg={6} sm={12}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('total_production_2')}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: palette.textPrimary,
                          paddingTop: '10px',
                        }}
                      >
                        {formatNumberToShortScale(
                          convertToTwoDecimalPlaces(data.haulerTon.toString())
                        )}
                        <span
                          style={{
                            color: palette.textSecondary,
                          }}
                        >
                          {' '}
                          {t('tons_unit')}
                        </span>
                      </Typography>
                      <Typography
                        sx={{
                          paddingTop: '30px',
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('current_daily_average_7d')}
                      </Typography>
                      {isLoadingFleetPeriodSummary ? (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {t('cdp_loading')}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {isLoadingFleetPeriodSummary
                            ? 'Loading...'
                            : formatNumberToShortScale(
                                convertToTwoDecimalPlaces(
                                  (currentDailyAverage ?? 0).toString()
                                )
                              )}
                          <span
                            style={{
                              color: palette.textSecondary,
                            }}
                          >
                            {' '}
                            {t('tons_per_day')}
                          </span>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {hasMetTarget ? (
                    <>
                      <Box
                        sx={{
                          textAlign: 'center',
                          border: `1px solid ${palette.cardBorderColor}`,
                          borderRadius: '5px',
                          marginTop: '15px',
                          padding: '10px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '90%',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <CheckCircleIcon
                            sx={{ color: '#029161', marginRight: '4px' }}
                          />
                          {t('avg_productive_hours_are_on_target')}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box
          sx={{
            marginTop: '50px', // Pushes the box to the bottom
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'right',
              color: palette.textSecondary,
              marginBottom: '4px',
            }}
          >
            {t('averages_shown_above_are_based_on_last_7_days_recent_most_da')}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'right',
              color: palette.textSecondary,
            }}
          >
            {t('ytd_numbers_are_based_on_real_time_data_received_from_assets')}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

export default HaulerProductionDetails;
